import { Box, ScrollArea, Text, UnstyledButton } from '@mantine/core'
import { StaffPersona } from '@features/personas/components/StaffPersona'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { SuggestionProps } from '@tiptap/suggestion'

export type StaffMentionItem = { initials: string; name: string; unavailable?: boolean }
export type MentionAttributes = { id: string; label: string }
export type StaffMentionSuggestionsHandle = { onKeyDown: (event: KeyboardEvent) => boolean }

export const StaffMentionSuggestions = forwardRef<StaffMentionSuggestionsHandle, SuggestionProps<StaffMentionItem, MentionAttributes>>(function StaffMentionSuggestions(props, ref) {
    const [selected, setSelected] = useState(0)
    const viewport = useRef<HTMLDivElement>(null)
    const items = props.items
    const unavailable = props.loading || !items.length || items[0].unavailable
    const active = Math.min(selected, Math.max(0, items.length - 1))
    function choose(index: number) {
        const person = items[index]
        if (!unavailable && person) props.command({ id: person.initials, label: person.name })
    }
    useEffect(() => { setSelected(0) }, [props.query, items])
    useEffect(() => {
        const container = viewport.current
        const button = container?.querySelector<HTMLElement>(`[data-staff-index="${active}"]`)
        if (!container || !button) return
        if (button.offsetTop < container.scrollTop) container.scrollTo({ top: button.offsetTop })
        else if (button.offsetTop + button.offsetHeight > container.scrollTop + container.clientHeight)
            container.scrollTo({ top: button.offsetTop + button.offsetHeight - container.clientHeight })
    }, [active])
    useImperativeHandle(ref, () => ({ onKeyDown: event => {
        if (props.loading && ['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) return true
        if (unavailable) return false
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            setSelected((active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length)
            return true
        }
        if (event.key === 'Enter') { choose(active); return true }
        return false
    } }))
    return <Box bg="var(--mantine-color-body)" p={4} style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8, boxShadow: 'var(--mantine-shadow-md)', width: 'min(360px, 90vw)' }}>
        <ScrollArea.Autosize mah={260} viewportRef={viewport} type="auto" scrollbars="y" offsetScrollbars>
            <Box role="listbox" aria-label="Staff to tag" pos="relative">
                {unavailable ? <Text p="xs" size="sm" role="status">{props.loading ? 'Loading staff…' : items[0]?.name ?? 'No matching staff'}</Text>
                    : items.map((person, index) => <UnstyledButton key={person.initials} role="option" aria-label={person.name} aria-selected={index === active} data-staff-index={index}
                        w="100%" p="xs" style={{ borderRadius: 4, background: index === active ? 'var(--mantine-color-blue-light)' : undefined }}
                        onMouseDown={event => event.preventDefault()} onMouseEnter={() => setSelected(index)} onClick={() => choose(index)}>
                        <StaffPersona identifier={{ type: 'initials', value: person.initials }} clickable={false} hoverable={false} initialsStyle="dimmed" />
                    </UnstyledButton>)}
            </Box>
        </ScrollArea.Autosize>
    </Box>
})
